import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Truck, Leaf, ShieldCheck, Heart, Play } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import ProductCard from "../components/ProductCard";
import SafeImage from "../components/SafeImage";
import SizeGuideModal from "../components/SizeGuideModal";
import ProductReviews from "../components/ProductReviews";
import { useCart } from "../context/CartContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { addToWishlist, removeFromWishlist } from "../lib/customerApi";
import SEO from "../components/SEO";
import { getProduct, getRelated, formatINR } from "../lib/api";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState("");
  const [activeImage, setActiveImage] = useState(0);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const { addItem } = useCart();
  const { user, refresh } = useCustomerAuth();
  const navigate = useNavigate();

  const inWishlist = !!(user && user !== false && user.wishlist?.includes(slug));

  const toggleWishlist = async () => {
    if (!user || user === false) {
      navigate(`/login?redirect=/shop/${slug}`);
      return;
    }
    try {
      if (inWishlist) {
        await removeFromWishlist(slug);
        toast.success("Removed from wishlist");
      } else {
        await addToWishlist(slug);
        toast.success("Added to wishlist");
      }
      await refresh();
    } catch {
      toast.error("Could not update wishlist");
    }
  };

  useEffect(() => {
    setProduct(null);
    setSize("");
    setActiveImage(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
    getProduct(slug).then(setProduct).catch(() => {});
    getRelated(slug).then(setRelated).catch(() => {});
  }, [slug]);

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="pdp-loading">
        <div className="grid md:grid-cols-2 gap-10">
          <div className="aspect-[3/4] bg-[#b8af92] animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-[#b8af92] animate-pulse" />
            <div className="h-6 bg-[#b8af92] animate-pulse w-2/3" />
            <div className="h-40 bg-[#b8af92] animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  const handleAdd = () => {
    if (!size) {
      toast.error("Please select a size first.");
      return;
    }
    addItem(product, size, 1);
    toast.success(`${product.name} added`, {
      description: `Size ${size} · ${formatINR(product.price)}`,
    });
  };

  return (
    <div className="bg-[#c3ba9e]" data-testid={`pdp-${product.slug}`}>
      <SEO
        title={product.name}
        description={product.description?.slice(0, 160)}
        image={product.images?.[0]}
        type="product"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-6">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#4A4A4A] hover:text-[#1A1A1A]"
          data-testid="pdp-back-link"
        >
          <ChevronLeft size={14} /> Back to shop
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14 grid md:grid-cols-2 gap-10 md:gap-16">
        {/* Gallery */}
        <div>
          <div className="relative aspect-[3/4] bg-[#b8af92] overflow-hidden mb-4 group">
            <SafeImage
              key={activeImage}
              src={product.images[activeImage]}
              alt={product.name}
              className="w-full h-full object-cover transition-opacity duration-300"
              data-testid="pdp-main-image"
            />
            {product.images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveImage((i) => (i - 1 + product.images.length) % product.images.length)}
                  className="absolute top-1/2 left-3 -translate-y-1/2 w-10 h-10 bg-[#c3ba9e]/85 hover:bg-[#c3ba9e] text-[#1A1A1A] flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                  aria-label="Previous image"
                  data-testid="pdp-prev-image"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => setActiveImage((i) => (i + 1) % product.images.length)}
                  className="absolute top-1/2 right-3 -translate-y-1/2 w-10 h-10 bg-[#c3ba9e]/85 hover:bg-[#c3ba9e] text-[#1A1A1A] flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                  aria-label="Next image"
                  data-testid="pdp-next-image"
                >
                  <ChevronLeft size={18} className="rotate-180" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {product.images.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setActiveImage(i)}
                      className={`h-1 transition-all ${i === activeImage ? "w-6 bg-[#1A1A1A]" : "w-3 bg-[#1A1A1A]/40"}`}
                      aria-label={`Go to image ${i + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="grid grid-cols-4 gap-3">
            {product.images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                className={`aspect-square overflow-hidden border-2 ${
                  activeImage === i ? "border-[#1A1A1A]" : "border-transparent"
                }`}
                data-testid={`pdp-thumb-${i}`}
              >
                <SafeImage src={img} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Info — sticky on desktop */}
        <div className="md:sticky md:top-28 self-start">
          {product.is_new && <p className="eyebrow text-[#1A1A1A] mb-3">New Arrival</p>}
          <h1 className="font-serif-display text-4xl md:text-5xl leading-[1.05]" data-testid="pdp-title">
            {product.name}
          </h1>
          <p className="text-sm text-[#7A7A7A] mt-2 tracking-wider">
            {product.color} · {product.fabric}
          </p>

          <div className="flex items-baseline gap-3 mt-6">
            <p className="text-2xl font-body" data-testid="pdp-price">
              {formatINR(product.price)}
            </p>
            {product.compare_at_price && (
              <p className="text-[#7A7A7A] line-through font-body text-sm">
                {formatINR(product.compare_at_price)}
              </p>
            )}
          </div>
          <p className="text-xs text-[#7A7A7A] mt-1">Inclusive of all taxes · Free shipping</p>

          <p className="mt-8 text-[15px] text-[#4A4A4A] leading-relaxed">{product.description}</p>

          {/* Size */}
          <div className="mt-10">
            <div className="flex items-center justify-between mb-3">
              <p className="eyebrow">Select Size</p>
              <button
                type="button"
                onClick={() => setSizeGuideOpen(true)}
                className="text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A] hover:text-[#6b6353] underline-offset-4 hover:underline"
                data-testid="pdp-size-guide"
              >
                Size Guide
              </button>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={`py-3 text-sm border transition-all ${
                    size === s
                      ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#c3ba9e]"
                      : "border-[#a89e80] hover:border-[#1A1A1A]"
                  }`}
                  data-testid={`pdp-size-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-8 space-y-3">
            <Button
              onClick={handleAdd}
              className="w-full bg-[#1A1A1A] hover:bg-[#000000] text-[#c3ba9e] rounded-none py-7 tracking-[0.22em] uppercase text-xs btn-shimmer"
              data-testid="pdp-add-to-cart"
            >
              Add to Cart · {formatINR(product.price)}
            </Button>
            <Button
              variant="outline"
              onClick={toggleWishlist}
              className={`w-full border-[#1A1A1A] rounded-none py-7 tracking-[0.22em] uppercase text-xs hover:bg-[#1A1A1A] hover:text-[#c3ba9e] flex items-center justify-center gap-2 ${
                inWishlist ? "bg-[#1A1A1A] text-[#c3ba9e]" : ""
              }`}
              data-testid="pdp-add-wishlist"
            >
              <Heart size={14} className={inWishlist ? "fill-current" : ""} />
              {inWishlist ? "Saved to Wishlist" : "Add to Wishlist"}
            </Button>
          </div>

          {/* Reassurance */}
          <div className="mt-10 grid grid-cols-3 gap-4 text-xs text-[#4A4A4A]">
            <div className="flex flex-col items-center text-center gap-2">
              <Truck size={18} className="text-[#1A1A1A]" />
              <span>Free shipping<br />across India</span>
            </div>
            <div className="flex flex-col items-center text-center gap-2">
              <Leaf size={18} className="text-[#1A1A1A]" />
              <span>Plastic-free<br />packaging</span>
            </div>
            <div className="flex flex-col items-center text-center gap-2">
              <ShieldCheck size={18} className="text-[#6b6353]" />
              <span>15-day<br />easy returns</span>
            </div>
          </div>

          {/* Accordions */}
          <Accordion type="single" collapsible className="mt-10 border-t border-[#a89e80]">
            <AccordionItem value="fabric" className="border-b border-[#a89e80]">
              <AccordionTrigger className="text-xs tracking-[0.22em] uppercase py-5 hover:no-underline" data-testid="pdp-accordion-fabric">
                Fabric &amp; Composition
              </AccordionTrigger>
              <AccordionContent className="text-[15px] text-[#4A4A4A] leading-relaxed pb-5">
                {product.fabric}. Woven and finished by hand. Variations in texture are a signature of our process and not a defect.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="care" className="border-b border-[#a89e80]">
              <AccordionTrigger className="text-xs tracking-[0.22em] uppercase py-5 hover:no-underline" data-testid="pdp-accordion-care">
                Care Instructions
              </AccordionTrigger>
              <AccordionContent className="text-[15px] text-[#4A4A4A] leading-relaxed pb-5">
                {product.care}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="shipping" className="border-b border-[#a89e80]">
              <AccordionTrigger className="text-xs tracking-[0.22em] uppercase py-5 hover:no-underline" data-testid="pdp-accordion-shipping">
                Shipping &amp; Returns
              </AccordionTrigger>
              <AccordionContent className="text-[15px] text-[#4A4A4A] leading-relaxed pb-5">
                Dispatched within 2 working days. Delivered in 3–7 days pan-India. Easy returns within 15 days of delivery on unworn pieces.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Product video reel */}
      {product.videos && product.videos.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-10" data-testid="pdp-video-section">
          <div className="flex items-center gap-2 mb-5">
            <Play size={14} className="text-[#1A1A1A]" />
            <p className="eyebrow">The Piece, In Motion</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {product.videos.map((src, i) => (
              <video
                key={i}
                src={src}
                className="w-full aspect-[3/4] object-cover bg-[#b8af92]"
                controls
                playsInline
                preload="metadata"
                data-testid={`pdp-video-${i}`}
              />
            ))}
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="pdp-related">
          <p className="eyebrow text-center">You may also like</p>
          <h2 className="font-serif-display text-3xl md:text-4xl text-center mt-2 mb-12">
            Pieces in the same spirit
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      <SizeGuideModal
        open={sizeGuideOpen}
        onClose={() => setSizeGuideOpen(false)}
        category={product.category}
      />

      <ProductReviews productSlug={product.slug} />
    </div>
  );
}
